import React, { useMemo, useState } from 'react';

import { Badge, Popover } from '@mui/material';
import { DropdownSelectButton } from 'src/components';
import type { BoardLocalState } from 'src/components/workflow/board/Board';
import type { ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import {
  BoardAssigneeFilterContent,
  BoardLabelsFilterContent,
  BoardPriorityFilterContent,
} from './filter-contents';

const useStyles = makeStyles()(theme => ({
  filterBadge: {
    '& .MuiBadge-badge': {
      right: 2,
      top: 2,
    },
  },
  paper: {
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxHeight: 250,
    minWidth: 189,
    padding: theme.spacing(1, 0),
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
}));

interface BoardFilterConfig {
  field: 'labels' | 'permissions' | 'priority';
  id: string;
  label: string;
  type: 'labels' | 'assignee' | 'priority';
  icon?: React.ReactNode;
}

interface BoardFilterOptionProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  filter: BoardFilterConfig;
}

function getActiveFilterCount(
  boardState: BoardLocalState,
  field: BoardFilterConfig['field']
): number {
  switch (field) {
    case 'labels':
      return boardState.filterLabels.length;
    case 'permissions':
      return boardState.filterAssignees.length;
    case 'priority':
      return boardState.filterPriorities?.length ?? 0;
  }
}

export function BoardFilterOption({
  boardStateTuple,
  filter,
}: BoardFilterOptionProps): JSX.Element {
  const { classes } = useStyles();
  const [boardState] = boardStateTuple;
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const activeCount = useMemo(
    () => getActiveFilterCount(boardState, filter.field),
    [boardState, filter.field]
  );

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Badge badgeContent={activeCount} className={classes.filterBadge} color="primary">
        <DropdownSelectButton
          ref={setAnchorEl}
          icon={filter.icon}
          label={filter.label}
          selected={isOpen}
          variant="outlined"
          onClick={() => setIsOpen(!isOpen)}
        />
      </Badge>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={isOpen}
        slotProps={{ paper: { className: classes.paper } }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        onClose={handleClose}
      >
        <div className={classes.popoverContent}>
          <BoardFilterContent boardStateTuple={boardStateTuple} filter={filter} />
        </div>
      </Popover>
    </>
  );
}

interface BoardFilterContentProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  filter: BoardFilterConfig;
}

function BoardFilterContent({ boardStateTuple, filter }: BoardFilterContentProps): JSX.Element {
  switch (filter.type) {
    case 'priority':
      return <BoardPriorityFilterContent boardStateTuple={boardStateTuple} />;
    case 'labels':
      return <BoardLabelsFilterContent boardStateTuple={boardStateTuple} />;
    case 'assignee':
      return <BoardAssigneeFilterContent boardStateTuple={boardStateTuple} />;
  }
}
